import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Avatar,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormLabel,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Alert,
  Link,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  CameraAlt as CameraIcon,
  Lock as LockIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import toast from 'react-hot-toast'
import { useLoading } from '../../hooks'
import { employeeApi } from '../../api/employeeApi'
import { shiftApi } from '../../api/shiftApi'


const employeeSchema = yup.object({
  firstName: yup.string()
    .required('First name is required')
    .matches(/^[a-zA-Z\s]+$/, 'First name can only contain letters and spaces'),
  lastName: yup.string()
    .required('Last name is required')
    .matches(/^[a-zA-Z\s]+$/, 'Last name can only contain letters and spaces'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits'),
  gender: yup.string().required('Gender is required'),
  department: yup.string().required('Department is required'),
  position: yup.string().required('Position is required'),
  joinDate: yup.string().required('Join date is required'),
  salary: yup.number().positive('Must be positive').required('Salary is required'),
  address: yup.string()
    .required('Address is required')
    .min(10, 'Address must be at least 10 characters')
    .matches(/^[a-zA-Z0-9\s,.\-#/]+$/, 'Address contains invalid characters'),
  emergencyContact: yup.string()
    .required('Emergency contact is required')
    .matches(/^[a-zA-Z\s]+$/, 'Contact name can only contain letters and spaces'),
  emergencyPhone: yup.string()
    .required('Emergency phone is required')
    .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits'),
  workShift: yup.string().required('Work shift is required'),
  employeeType: yup.string().required('Employee type is required'),
})

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']


const departments = [
  'HR', 'IT', 'Sales', 'Marketing', 'Finance', 'Operations',
  'Production', 'Quality Assurance', 'Research & Development', 'Customer Support'
]

const positions = [
  'Manager', 'Team Lead', 'Senior Developer', 'Developer', 'Designer',
  'Analyst', 'Executive', 'Associate', 'Trainee', 'Intern'
]

const AddEmployee = ({ initialData: propInitialData = null, mode: propMode = 'add', onCancel }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const mode = id ? 'edit' : propMode
  const [initialData, setInitialData] = useState(propInitialData)

  useEffect(() => {
    if (propInitialData) {
      setInitialData(propInitialData)
    }
  }, [propInitialData])
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { loading, startLoading, stopLoading } = useLoading()
  const [activeStep, setActiveStep] = useState(0)
  const [photo, setPhoto] = useState(initialData?.photo || null)
  const [workingDays, setWorkingDays] = useState(
    initialData?.workingDays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
  )
  const [shifts, setShifts] = useState([])
  const [shiftsLoaded, setShiftsLoaded] = useState(false)
  const [formChanged, setFormChanged] = useState(false)
  const [isProcessingNext, setIsProcessingNext] = useState(false)
  const [lastStepChangeTime, setLastStepChangeTime] = useState(0)

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    trigger,
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(employeeSchema),
    defaultValues: initialData || {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      gender: '',
      department: '',
      position: '',
      address: '',
      emergencyContact: '',
      emergencyPhone: '',
      employeeType: 'onroll',
      workShift: '',
      workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    },
    context: { isEdit: mode === 'edit' },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      if (id && !propInitialData) {
        startLoading()
        try {
          const response = await employeeApi.getEmployeeDetails(id)
          const data = response.data

          // Parse working_days if needed
          let parsedWorkingDays = []
          try {
            if (typeof data.working_days === 'string') {
              parsedWorkingDays = JSON.parse(data.working_days)
            } else if (Array.isArray(data.working_days)) {
              parsedWorkingDays = data.working_days
            }
          } catch (e) {
            console.error('Error parsing working days', e)
          }

          const mappedData = {
            id: data.employee_id,
            firstName: data.first_name,
            lastName: data.last_name,
            email: data.email,
            phone: data.phone,
            department: data.department,
            position: data.designation, // Mapped from designation
            joinDate: data.join_date,
            salary: data.salary,
            address: data.address,
            emergencyContact: data.emergency_contact,
            emergencyPhone: data.emergency_phone,
            gender: data.gender || '',
            workShift: data.work_shift_id || data.work_shift,
            employeeType: data.employee_type,
            workingDays: parsedWorkingDays.length ? parsedWorkingDays : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            maritalStatus: data.marital_status,
            bloodGroup: data.blood_group,
            photo: data.photo
          }

          setInitialData(mappedData)
          setPhoto(mappedData.photo)
          setWorkingDays(mappedData.workingDays)

          reset(mappedData)
        } catch (error) {
          console.error('Failed to fetch employee details', error)
          toast.error('Failed to load employee details')
          navigate('/org/employees')
        } finally {
          stopLoading()
        }
      }
    }

    fetchEmployeeDetails()

    const fetchShifts = async () => {
      try {
        const response = await shiftApi.listShifts()
        setShifts(response.data.shifts || [])
      } catch (error) {
        console.error('Failed to fetch shifts', error)
      } finally {
        setShiftsLoaded(true)
      }
    }
    fetchShifts()
  }, [id, propInitialData, reset, navigate])

  useEffect(() => {
    setFormChanged(isDirty)
  }, [isDirty])

  useEffect(() => {
    if (initialData) {
      let workShiftValue = initialData.workShift;

      // If workShift is a string (e.g. "Morning shift (9am to 5pm)") and we have shifts loaded, find the ID
      if (typeof workShiftValue === 'string' && shifts.length > 0) {
        const matchingShift = shifts.find(s => {
          const renderedLabel = `${s.shift_name} (${s.start_time.substring(0, 5)} - ${s.end_time.substring(0, 5)})`;
          return s.shift_name === workShiftValue || renderedLabel === workShiftValue;
        });
        if (matchingShift) {
          workShiftValue = matchingShift.id;
        }
      }

      reset({
        ...initialData,
        workShift: workShiftValue,
        password: '', // Clear password field for edit mode
        workingDays: initialData.workingDays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
      })
      setWorkingDays(initialData.workingDays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'])
      setPhoto(initialData.photo || null)
    }
  }, [initialData, reset, shifts])

  const steps = ['Basic Information', 'Job Details', 'Work Schedule', 'Additional Information']

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPhoto(reader.result)
        setValue('photo', reader.result, { shouldValidate: true })
        setFormChanged(true)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleWorkingDayChange = (day) => {
    const newDays = workingDays.includes(day)
      ? workingDays.filter(d => d !== day)
      : [...workingDays, day]
    setWorkingDays(newDays)
    setValue('workingDays', newDays, { shouldValidate: true })
    setFormChanged(true)
  }

  const handleNext = async () => {
    // Prevent multiple simultaneous calls
    if (isProcessingNext) {
      return
    }

    setIsProcessingNext(true)
    let isValid = false

    switch (activeStep) {
      case 0:
        isValid = await trigger(['firstName', 'lastName', 'email', 'phone', 'address'])
        break
      case 1:
        isValid = await trigger(['department', 'position', 'employeeType', 'joinDate', 'salary'])
        break
      case 2:
        isValid = await trigger(['workShift'])
        break
      case 3:
        isValid = await trigger(['emergencyContact', 'emergencyPhone'])
        break
      default:
        isValid = true
    }

    if (isValid) {
      setLastStepChangeTime(Date.now())
      setActiveStep((prevStep) => prevStep + 1)
    } else {
      toast.error('Please fill in all required fields correctly before proceeding')
    }

    setIsProcessingNext(false)
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const onSubmit = async (data, event) => {
    // Prevent auto-submit immediately after step transition (likely from lingering Enter key)
    const timeSinceStepChange = Date.now() - lastStepChangeTime
    if (timeSinceStepChange < 500) {
      event?.preventDefault()
      return
    }

    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (loading) return

    startLoading()

    try {
      if (mode === 'edit' && !formChanged) {
        toast('No changes detected')
        navigate(`/org/employees/${initialData.id}`)
        stopLoading()
        return
      }

      const payload = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone,
        department: data.department,
        designation: data.position,
        join_date: data.joinDate,
        salary: Number(data.salary),
        address: data.address,
        emergency_contact: data.emergencyContact,
        emergency_phone: data.emergencyPhone,
        gender: data.gender,
        work_shift: data.workShift,
        employee_type: data.employeeType,
        working_days: data.workingDays,
        marital_status: data.maritalStatus || 'unmarried',
        blood_group: data.bloodGroup,
        status: initialData?.status || 'active',
        photo: photo || "" // base64 string
      }

      let response;
      if (mode === 'edit' && initialData?.id) {
        response = await employeeApi.updateEmployee({ ...payload, employee_id: initialData.id })
      } else {
        response = await employeeApi.addEmployee(payload)
      }

      toast.success(mode === 'edit' ? 'Employee updated successfully!' : 'Employee added successfully!')

      stopLoading()

      if (mode === 'edit' && initialData?.id) {
        navigate('/org/employees')
      } else {
        navigate('/org/employees')
      }

    } catch (error) {
      stopLoading()
      console.error('Error saving employee:', error)
      let errorMessage = 'Failed to save employee data'
      const detail = error.response?.data?.detail
      if (typeof detail === 'string') {
        errorMessage = detail
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(err => err.msg || (typeof err === 'object' ? JSON.stringify(err) : err)).join(', ')
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }
      toast.error(errorMessage)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // Only allow Enter to move to next step, never auto-submit
      if (activeStep < steps.length - 1) {
        handleNext()
      }
      // On last step, do nothing - user must click the submit button explicitly
    }
  }

  const handleFieldChange = (fieldName) => {
    setFormChanged(true)
  }

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar
                  src={photo}
                  sx={{ width: 150, height: 150, mb: 2, border: '2px solid #e0e0e0' }}
                >
                  {!photo && <CameraIcon sx={{ fontSize: 60 }} />}
                </Avatar>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="photo-upload"
                  type="file"
                  onChange={handlePhotoUpload}
                />
                <label htmlFor="photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                  >
                    Upload Photo
                  </Button>
                </label>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Grid container spacing={2}>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="First Name *"
                    {...register('firstName', {
                      onChange: () => handleFieldChange('firstName')
                    })}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Last Name *"
                    {...register('lastName', {
                      onChange: () => handleFieldChange('lastName')
                    })}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email *"
                    type="email"
                    {...register('email', {
                      onChange: () => handleFieldChange('email')
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone *"
                    {...register('phone', {
                      onChange: () => handleFieldChange('phone')
                    })}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth error={!!errors.gender}>
                    <InputLabel>Gender *</InputLabel>
                    <Select
                      label="Gender *"
                      value={watch('gender') || ''}
                      {...register('gender', {
                        onChange: () => handleFieldChange('gender')
                      })}
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                      <MenuItem value="prefer not to say">Prefer not to say</MenuItem>
                    </Select>
                    {errors.gender && (
                      <Typography color="error" variant="caption">
                        {errors.gender.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Address *"
                    multiline
                    rows={2}
                    {...register('address', {
                      onChange: () => handleFieldChange('address')
                    })}
                    error={!!errors.address}
                    helperText={errors.address?.message}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )

      case 1:
        return (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.department}>
                <InputLabel>Department *</InputLabel>
                <Select
                  label="Department *"
                  value={watch('department') || ''}
                  {...register('department', {
                    onChange: () => handleFieldChange('department')
                  })}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
                {errors.department && (
                  <Typography color="error" variant="caption">
                    {errors.department.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.position}>
                <InputLabel>Position *</InputLabel>
                <Select
                  label="Position *"
                  value={watch('position') || ''}
                  {...register('position', {
                    onChange: () => handleFieldChange('position')
                  })}
                >
                  {positions.map((pos) => (
                    <MenuItem key={pos} value={pos}>
                      {pos}
                    </MenuItem>
                  ))}
                </Select>
                {errors.position && (
                  <Typography color="error" variant="caption">
                    {errors.position.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Employee Type *</InputLabel>
                <Select
                  label="Employee Type *"
                  value={watch('employeeType') || ''}
                  {...register('employeeType', {
                    onChange: () => handleFieldChange('employeeType')
                  })}
                  error={!!errors.employeeType}
                >
                  <MenuItem value="onroll">On Roll</MenuItem>
                  <MenuItem value="outsource">Outsource</MenuItem>
                </Select>
              </FormControl>
            </Grid>



            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Join Date *"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...register('joinDate', {
                  onChange: () => handleFieldChange('joinDate')
                })}
                error={!!errors.joinDate}
                helperText={errors.joinDate?.message}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Salary *"
                type="number"
                InputProps={{ startAdornment: '₹' }}
                {...register('salary', {
                  onChange: () => handleFieldChange('salary')
                })}
                error={!!errors.salary}
                helperText={errors.salary?.message}
              />
            </Grid>
          </Grid>
        )

      case 2:
        return (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {shiftsLoaded && shifts.length === 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert
                  severity="warning"
                  sx={{ borderRadius: 2 }}
                  action={
                    <Link
                      href="/org/shifts"
                      underline="always"
                      sx={{ fontWeight: 600, whiteSpace: 'nowrap', alignSelf: 'center' }}
                    >
                      Create Shift
                    </Link>
                  }
                >
                  <strong>No shifts available.</strong> You must create at least one work shift before adding an employee.
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth error={!!errors.workShift} disabled={shiftsLoaded && shifts.length === 0}>
                <InputLabel>Work Shift *</InputLabel>
                <Select
                  label="Work Shift *"
                  value={watch('workShift') || ''}
                  {...register('workShift', {
                    onChange: () => handleFieldChange('workShift')
                  })}
                >
                  {shifts.map((shift) => (
                    <MenuItem key={shift.id} value={shift.id}>
                      {shift.shift_name} ({shift.start_time.substring(0, 5)} - {shift.end_time.substring(0, 5)})
                    </MenuItem>
                  ))}
                </Select>
                {errors.workShift && (
                  <Typography color="error" variant="caption">
                    {errors.workShift.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 1, color: 'text.primary', fontWeight: '500' }}>
                  Working Days *
                </FormLabel>
                <Paper elevation={0} sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : '#f8f9fa', borderRadius: 1 }}>
                  <FormGroup row sx={{ justifyContent: 'space-between' }}>
                    {daysOfWeek.map((day) => (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            checked={workingDays.includes(day)}
                            onChange={() => handleWorkingDayChange(day)}
                            name={day}
                            size="small"
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {day.substring(0, 3)}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    ))}
                  </FormGroup>
                </Paper>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Selected: {workingDays.join(', ')}
                </Typography>
              </FormControl>
            </Grid>
          </Grid>
        )

      case 3:
        return (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Emergency Contact Name *"
                {...register('emergencyContact', {
                  onChange: () => handleFieldChange('emergencyContact')
                })}
                error={!!errors.emergencyContact}
                helperText={errors.emergencyContact?.message}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Emergency Contact Phone *"
                {...register('emergencyPhone', {
                  onChange: () => handleFieldChange('emergencyPhone')
                })}
                error={!!errors.emergencyPhone}
                helperText={errors.emergencyPhone?.message}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Blood Group"
                placeholder="e.g., O+"
                {...register('bloodGroup', {
                  onChange: () => handleFieldChange('bloodGroup')
                })}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Marital Status</InputLabel>
                <Select
                  label="Marital Status"
                  value={watch('maritalStatus') || ''}
                  {...register('maritalStatus', {
                    onChange: () => handleFieldChange('maritalStatus')
                  })}
                >
                  <MenuItem value="unmarried">Single</MenuItem>
                  <MenuItem value="married">Married</MenuItem>
                  <MenuItem value="divorced">Divorced</MenuItem>
                  <MenuItem value="widowed">Widowed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                placeholder="Additional notes about the employee"
                {...register('notes', {
                  onChange: () => handleFieldChange('notes')
                })}
              />
            </Grid>
          </Grid>
        )

      default:
        return 'Unknown step'
    }
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate(-1)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                  {mode === 'edit' ? 'Edit Employee' : 'Add New Employee'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {mode === 'edit' ? 'Update employee details' : 'Fill in employee details step by step'}
                </Typography>
                {mode === 'edit' && !formChanged && (
                  <Typography variant="caption" color="textSecondary">
                    No changes made yet
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Stepper */}
        <Box sx={{ width: '100%', overflowX: 'auto', mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel={isSmallScreen} sx={{ minWidth: { xs: '100%', md: 'auto' } }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
          {/* Step Content */}
          <Box sx={{ mb: 4 }}>
            {getStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={onCancel || (() => navigate('/org/employees'))}
              type="button"
              disabled={loading}
            >
              Cancel
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep > 0 && (
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  type="button"
                  disabled={loading}
                >
                  Back
                </Button>
              )}

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  type="submit"
                  startIcon={<SaveIcon />}
                  sx={{ minWidth: 120 }}
                  disabled={loading || (mode === 'edit' && !formChanged)}
                >
                  {loading ? 'Saving...' : (mode === 'edit' ? 'Update Employee' : 'Save Employee')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  type="button"
                  sx={{ minWidth: 120 }}
                  disabled={loading}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </form>

        {/* Quick Tips Card */}
        <Card sx={{ mt: 3, backgroundColor: theme.palette.mode === 'dark' ? 'action.hover' : '#f5f5f5' }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              💡 {mode === 'edit' ? 'Editing Tips' : 'Quick Tips'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {mode === 'edit' ? (
                <>
                  • Update only necessary fields<br />
                  • Leave password blank to keep current password<br />
                  • Click "Update Employee" button to save changes<br />
                  • Form will not auto-submit
                </>
              ) : (
                <>
                  • Ensure all required fields are filled correctly<br />
                  • Upload a clear photo for face recognition<br />
                  • Double-check contact information<br />
                  • After saving, you can enroll face biometrics
                </>
              )}
            </Typography>
          </CardContent>
        </Card>
      </Paper>
    </Box>
  )
}

export default AddEmployee